import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, Pause, ChevronLeft, ChevronRight, Code, Eye } from "lucide-react";
import CodeBlock from "@/components/CodeBlock";
import { useTheme } from "@/contexts/ThemeContext";

interface PPTPayload {
  title: string;
  totalSlides: number;
  slides: string[]; // Array of complete HTML documents
  assets?: string[];
}

// Fixed slide dimensions (standard 16:9)
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;

const SlidesViewer: React.FC = () => {
  const location = useLocation() as any;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [ppt, setPpt] = useState<PPTPayload | null>(null);
  const [viewMode, setViewMode] = useState<"preview" | "code">("preview");
  const [autoplay, setAutoplay] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [scale, setScale] = useState<number>(1);
  // containerRef now wraps the area that should hold the scaled slide -
  // its available WIDTH and HEIGHT (after flex layout accounts for the
  // header and the nav controls) both drive the scale calculation.
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);

  // Load PPT from navigation state
  useEffect(() => {
    const fromState: PPTPayload | undefined = location?.state?.ppt;
    if (fromState && fromState.slides && fromState.slides.length > 0) {
      setPpt(fromState);
    }
  }, [location?.state]);

  const slides = useMemo(() => (ppt?.slides || []), [ppt]);

  const processedSlides = useMemo(() => {
    const origin = window.location.origin;
    return slides.map((slideHtml) => {
      if (!slideHtml) return "";
      const fallbackScript = `
<script>
  (function() {
    const FALLBACK_IMAGE = "${origin}/placeholder_slides-ai.jpg";
    const patchImg = (img) => {
      if (img.dataset.__oreogPatched === '1') return;
      img.dataset.__oreogPatched = '1';
      const rescue = () => {
        const attempts = Number(img.dataset.__oreogAttempts || '0');
        if (attempts >= 5) return;
        img.dataset.__oreogAttempts = String(attempts + 1);
        img.src = FALLBACK_IMAGE;
      };
      img.addEventListener('error', rescue);
      const check = () => {
        if (img.complete && img.naturalWidth === 0) {
          rescue();
        }
      };
      setTimeout(check, 1200);
      setTimeout(check, 3500);
    };
    const patchAll = () => {
      document.querySelectorAll('img').forEach(patchImg);
    };
    patchAll();
    const obs = new MutationObserver(patchAll);
    obs.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });
  })();
</script>
`;
      if (slideHtml.includes("</body>")) {
        return slideHtml.replace("</body>", `${fallbackScript}</body>`);
      }
      return `${slideHtml}${fallbackScript}`;
    });
  }, [slides]);

  // Calculate scale based on BOTH available width and available height,
  // so the slide shrinks to fit whichever dimension is tighter instead
  // of only fitting width and overflowing vertically.
  const updateScale = useCallback(() => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      if (clientWidth === 0 || clientHeight === 0) return;
      const scaleByWidth = clientWidth / SLIDE_WIDTH;
      const scaleByHeight = clientHeight / SLIDE_HEIGHT;
      const newScale = Math.min(scaleByWidth, scaleByHeight, 1);
      setScale(newScale);
    }
  }, []);

  // Handle resize with ResizeObserver for better initial load
  useEffect(() => {
    // Delay initial update to ensure DOM is ready
    const initialTimeout = setTimeout(updateScale, 50);

    // Use ResizeObserver for more reliable sizing
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateScale);

    return () => {
      clearTimeout(initialTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [updateScale]);

  // Recalculate whenever we switch back into preview mode, since the
  // container may not have existed (and thus had no size) while in code view.
  useEffect(() => {
    if (viewMode === "preview") {
      const t = setTimeout(updateScale, 50);
      return () => clearTimeout(t);
    }
  }, [viewMode, updateScale]);

  // Autoplay for slides
  useEffect(() => {
    if (!autoplay || slides.length <= 1) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = window.setInterval(() => {
      setCurrentSlide(prev => (prev < slides.length - 1 ? prev + 1 : 0));
    }, 5000) as unknown as number;

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [autoplay, slides.length]);



  if (!ppt) {
    return (
      <div className="h-dvh flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="text-xl font-semibold">Slide not found</div>
          <div className="text-sm text-gray-500">The slide data for this link is missing or expired.</div>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => navigate("/")}>Go Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh w-full bg-background overflow-hidden flex flex-col">
      {/* Header - shrink-0 so it keeps its natural height and never gets squeezed */}
      <div className="shrink-0 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="mx-auto w-full max-w-6xl px-3 py-2 flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-xl h-8 w-8">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold truncate text-sm">{ppt.title || "Slides"}</div>
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant={viewMode === "preview" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("preview")}
              className="rounded-xl h-8 w-8"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "code" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("code")}
              className="rounded-xl h-8 w-8"
            >
              <Code className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main content - flex-1 min-h-0 is the key fix: it lets this area
          shrink to exactly the space left after the header instead of
          growing past the viewport and forcing a page scroll. */}
      <div className="mx-auto w-full max-w-6xl px-3 py-3 flex-1 min-h-0 flex flex-col overflow-hidden">
        {viewMode === "code" ? (
          /* Code View - this is the one place we DO want scrolling,
             since a deck can have many slides worth of code */
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="grid grid-cols-1 gap-4">
              {slides.map((slideHtml, idx) => (
                <div key={idx} className="w-full">
                  <div className="mb-2 font-semibold text-sm">Slide {idx + 1} of {slides.length}</div>
                  <CodeBlock code={slideHtml} language="html" isDarkMode={isDarkMode} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Scaled Slides Preview */
          <div className="flex-1 min-h-0 flex flex-col items-center">
            {/* This wrapper's clientWidth/clientHeight (after the nav bar
                below claims its own shrink-0 space) is what drives `scale`. */}
            <div
              ref={containerRef}
              className="relative w-full flex-1 min-h-0 flex items-center justify-center overflow-hidden"
            >
              <div
                className="relative rounded-xl overflow-hidden"
                style={{
                  width: SLIDE_WIDTH * scale,
                  height: SLIDE_HEIGHT * scale,
                }}
              >
                {/* The actual slide iframe at fixed dimensions, scaled down */}
                {processedSlides.map((slideHtml, idx) => (
                  <iframe
                    key={idx}
                    title={`Slide ${idx + 1}`}
                    className="absolute top-0 left-0 rounded-xl bg-white"
                    sandbox="allow-scripts allow-popups"
                    srcDoc={slideHtml || ''}
                    style={{
                      width: SLIDE_WIDTH,
                      height: SLIDE_HEIGHT,
                      transform: `scale(${scale})`,
                      transformOrigin: 'top left',
                      border: 'none',
                      opacity: currentSlide === idx ? 1 : 0,
                      transition: 'opacity 0.4s ease-in-out',
                      pointerEvents: currentSlide === idx ? 'auto' : 'none',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Navigation overlay - shrink-0 so it always keeps its own
                space and the slide area above shrinks around it */}
            <div className="shrink-0 flex items-center justify-center gap-3 mt-3">
              {/* Previous button */}
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-9 w-9"
                onClick={() => setCurrentSlide(prev => (prev > 0 ? prev - 1 : slides.length - 1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {/* Slide counter */}
              <span className="text-sm text-muted-foreground min-w-[60px] text-center">
                {currentSlide + 1} / {slides.length}
              </span>

              {/* Play/Pause button */}
              <Button
                variant={autoplay ? 'default' : 'outline'}
                size="icon"
                className="rounded-full h-9 w-9"
                onClick={() => setAutoplay(v => !v)}
              >
                {autoplay ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>

              {/* Next button */}
              <Button
                variant="outline"
                size="icon"
                className="rounded-full h-9 w-9"
                onClick={() => setCurrentSlide(prev => (prev < slides.length - 1 ? prev + 1 : 0))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlidesViewer;